import { useState, useCallback, useRef, useEffect } from "react";
import { SectionCitation, SourceVerificationState } from "../types/citations";
import { useAuthFetch } from "../../../utils/useAuthFetch";

const initialState: SourceVerificationState = {
  isPanelOpen: false,
  expandedRef: null,
  sectionHeights: {},
  citations: {},
  citationsLoaded: false,
  validationStatus: {},
  validationErrors: {},
};

export const useSourceVerification = (
  documentId: number | undefined,
  workspaceId: string,
  onSectionContentUpdate?: (sectionId: number, content: string) => void,
) => {
  const [state, setState] = useState<SourceVerificationState>(initialState);
  const authFetch = useAuthFetch();
  const authFetchRef = useRef(authFetch);
  const contentUpdateRef = useRef(onSectionContentUpdate);

  useEffect(() => {
    authFetchRef.current = authFetch;
  }, [authFetch]);

  useEffect(() => {
    contentUpdateRef.current = onSectionContentUpdate;
  }, [onSectionContentUpdate]);

  const fetchCitations = useCallback(
    async (retryCount = 0) => {
      if (!documentId) return;
      try {
        const data = await authFetchRef.current(
          `/api/workspace/meridian/documents/${documentId}/citations?workspace_id=${workspaceId}`,
          "fetch document citations",
        );
        const citationsBySection: Record<number, SectionCitation[]> = {};
        if (data?.citations) {
          for (const [sectionId, sectionCitations] of Object.entries(
            data.citations,
          )) {
            citationsBySection[Number(sectionId)] =
              sectionCitations as SectionCitation[];
          }
        }
        setState((prev) => {
          // Merge: prefer existing WebSocket-delivered citations over fetched data
          const merged = { ...citationsBySection };
          for (const [sectionId, existing] of Object.entries(prev.citations)) {
            if (existing.length > 0) {
              merged[Number(sectionId)] = existing;
            }
          }
          return {
            ...prev,
            citations: merged,
            citationsLoaded: true,
          };
        });
      } catch (err: unknown) {
        // Retry on abort (can happen when preload/other requests saturate connections)
        if (
          err instanceof DOMException &&
          err.name === "AbortError" &&
          retryCount < 2
        ) {
          setTimeout(
            () => fetchCitations(retryCount + 1),
            1000 * (retryCount + 1),
          );
          return;
        }
        console.error("Failed to fetch citations:", err);
        // Don't mark citationsLoaded on error so the next panel toggle retries
      }
    },
    [documentId, workspaceId],
  );

  const togglePanel = useCallback(() => {
    let shouldFetch = false;
    setState((prev) => {
      const opening = !prev.isPanelOpen;
      if (opening && !prev.citationsLoaded && documentId) {
        shouldFetch = true;
      }
      return { ...prev, isPanelOpen: opening };
    });
    if (shouldFetch) {
      fetchCitations();
    }
  }, [documentId, fetchCitations]);

  const expandRef = useCallback((sectionId: number, refId: string) => {
    setState((prev) => {
      // Toggle off if same ref is already expanded
      if (
        prev.expandedRef?.sectionId === sectionId &&
        prev.expandedRef?.refId === refId
      ) {
        return { ...prev, expandedRef: null };
      }
      return { ...prev, expandedRef: { sectionId, refId } };
    });
  }, []);

  const collapseRef = useCallback(() => {
    setState((prev) => ({ ...prev, expandedRef: null }));
  }, []);

  const updateSectionHeight = useCallback(
    (sectionId: number, height: number) => {
      setState((prev) => ({
        ...prev,
        sectionHeights: { ...prev.sectionHeights, [sectionId]: height },
      }));
    },
    [],
  );

  // Handle WebSocket Message 1: content ready, pre-validation
  const handleValidatingSection = useCallback((sectionId: number) => {
    setState((prev) => ({
      ...prev,
      validationStatus: { ...prev.validationStatus, [sectionId]: "validating" },
    }));
  }, []);

  // Handle WebSocket Message 2: citations finalized
  const handleCitationsReceived = useCallback(
    (
      sectionId: number,
      citations: SectionCitation[],
      validationError?: string,
    ) => {
      setState((prev) => {
        const newErrors = { ...prev.validationErrors };
        if (validationError) {
          newErrors[sectionId] = validationError;
        } else {
          delete newErrors[sectionId];
        }
        return {
          ...prev,
          citations: {
            ...prev.citations,
            [sectionId]: citations.map((c) => ({
              ...c,
              section_id: sectionId,
            })),
          },
          validationStatus: {
            ...prev.validationStatus,
            [sectionId]: "complete",
          },
          validationErrors: newErrors,
        };
      });
    },
    [],
  );

  // Clear citations for a section (used during regeneration/edit)
  const clearSectionCitations = useCallback((sectionId: number) => {
    setState((prev) => {
      const newCitations = { ...prev.citations };
      delete newCitations[sectionId];
      const newStatus = { ...prev.validationStatus };
      delete newStatus[sectionId];
      return { ...prev, citations: newCitations, validationStatus: newStatus };
    });
  }, []);

  // Poll for citation completion when sections are in "validating" state.
  // Covers both edit reverification and regeneration (no WebSocket).
  useEffect(() => {
    const validatingSections = Object.entries(state.validationStatus)
      .filter(([, status]) => status === "validating")
      .map(([id]) => Number(id));

    if (validatingSections.length === 0) return;

    const intervalId = setInterval(async () => {
      if (!documentId) return;
      try {
        const data = await authFetchRef.current(
          `/api/workspace/meridian/documents/${documentId}/citations?workspace_id=${workspaceId}`,
          "poll citations",
        );
        if (!data?.citations) return;

        // Collect content updates to fire AFTER setState (avoid side effects
        // inside the updater, which caused data-ref spans to never appear).
        const pendingContentUpdates: { sectionId: number; content: string }[] =
          [];

        setState((prev) => {
          const updates: Record<number, SectionCitation[]> = {};
          const statusUpdates: Record<number, "validating" | "complete"> = {};
          let changed = false;

          for (const sectionId of validatingSections) {
            const fetched = data.citations[String(sectionId)] as
              | SectionCitation[]
              | undefined;
            if (fetched && fetched.length > 0) {
              updates[sectionId] = fetched;
              statusUpdates[sectionId] = "complete";
              changed = true;

              const content = data.section_content?.[String(sectionId)];
              if (content) {
                pendingContentUpdates.push({ sectionId, content });
              }
            }
          }

          if (!changed) return prev;

          return {
            ...prev,
            citations: { ...prev.citations, ...updates },
            validationStatus: { ...prev.validationStatus, ...statusUpdates },
          };
        });

        // Push data-ref content into generation state after citation state settles
        for (const { sectionId, content } of pendingContentUpdates) {
          if (contentUpdateRef.current) {
            contentUpdateRef.current(sectionId, content);
          }
        }
      } catch {
        // Silently retry on next interval
      }
    }, 4000);

    return () => clearInterval(intervalId);
  }, [state.validationStatus, documentId, workspaceId]);

  return {
    ...state,
    togglePanel,
    expandRef,
    collapseRef,
    updateSectionHeight,
    handleValidatingSection,
    handleCitationsReceived,
    clearSectionCitations,
    fetchCitations,
  };
};
